import React from 'react'

import BaseTable from './BaseTable'

import moment from 'moment'

import General from '../../../utils/General'
import Currency from '../../../utils/Currency'

import RecurringCell from './cells/RecurringCell'
import PillCell from './cells/PillCell'
import CustomerCell from './cells/CustomerCell'

import DisplayCDC from '../splink/DisplayCDC'
import ExpanderCell from './cells/ExpanderCell'

const TYPE_FILTERS = {
  name: {
    api: 'recurring',
    display: 'recurring',
  },
  values: [
    {
      label: 'All Payments',
      value: null,
    },
    {
      label: 'Recurring',
      value: 'true',
    },
    {
      label: 'One-Off',
      value: 'false',
    },
  ],
}

const STATUS_FILTERS = {
  name: {
    api: 'type',
    display: 'type',
  },
  values: [
    {
      label: 'All Add-Ons',
      value: null,
    },
    {
      label: 'Take',
      value: 'take',
    },
    {
      label: 'Request',
      value: 'payment_request',
    },
    {
      label: 'Shop',
      value: 'shop',
    },
  ],
}

const FILTERS = [TYPE_FILTERS, STATUS_FILTERS]

export default class PayoutLineItemsTable extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      company: props.company,
      dateFrom: props.dateFrom,
      dateTo: props.dateTo,
      payout: props.payout,
      type: props.type,
      filters: FILTERS,
      showRefundModal: false,
    }

    this.table = React.createRef()
  }

  _getColumns() {
    let {
      payout,
      company
    } = this.state

    let columns = [
      {
        expander: true,
        Expander: ({ isExpanded, ...props }) => (
          <ExpanderCell
            isExpanded={isExpanded}
            show={() => {
              let payment = props.original
              let customer = payment.customer
              let showExpander =
                this.props.payout ||
                customer.shipping_address ||
                customer.note ||
                payment.answers.length > 0
              return showExpander || true // always show expander due to charge id
            }}
          />
        ),
      },
      {
        Header: 'Customer',
        id: 'customer.email',
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original
          let payment = payoutLineItem.payment || payoutLineItem.refund.payment
          return <CustomerCell customer={payment.customer} />
        },
        width: 160,
      },
      {
        Header: 'Processed',
        sortable: false,
        id: 'payment_plan.total',
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original
          let payment = payoutLineItem.payment || payoutLineItem.refund.payment
          let processed = Currency.format(payment.payment_plan.currency.symbol, payment.payment_plan.total)

          return (
            <div className="d-flex align-items-center">
              <div className="ml-2">
                <div className="text-dark-75 line-height-sm">{processed}</div>
              </div>
            </div>
          )
        },
      },
      {
        Header: 'Fees',
        sortable: false,
        id: 'fee',
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original

          let fee = "-"
          if(payoutLineItem.fees){
            fee = Currency.format(payout.currency.symbol, payoutLineItem.fees)
          }

          return (
            <div className="d-flex align-items-center">
              <div className="ml-2">
                <div className="text-dark-75 line-height-sm">{fee}</div>
              </div>
            </div>
          )
        },
      },
      {
        Header: 'Deposited',
        sortable: false,
        id: 'commission',
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original

          let net = Currency.format(payout.currency.symbol, payoutLineItem.amount)

          return (
            <div className="d-flex align-items-center">
              <div className="ml-2">
                <div className="text-dark-75 line-height-sm">{net}</div>
              </div>
            </div>
          )
        },
      },
      {
        Header: 'Type',
        id: 'type',
        sortable: false,
        exportable: false,
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original
          let payment = payoutLineItem.payment || payoutLineItem.refund.payment
          let paymentMethodType = payment.payment_instrument?.payment_method?.type
          let terminalEnabled = company.add_ons.terminals
          let takeTitle = paymentMethodType === "pos" ? "POS" : paymentMethodType === "soft_pos" ? "SoftPOS" : paymentMethodType === "cash" ? "Cash" : terminalEnabled ? "Online" : "Take"
          let text = payment.payment_plan.product
            ? 'Shop'
            : payment.payment_plan.payment_request
              ? 'Request'
              : takeTitle
          let className = payment.payment_plan.product
            ? 'label-light-info'
            : payment.payment_plan.payment_request
              ? 'label-light-danger'
              : 'label-light-primary'
          return <PillCell text={text} className={className} />
        },
      },
      {
        Header: 'Recurring',
        id: 'recurring',
        sortable: false,
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original
          let payment = payoutLineItem.payment || payoutLineItem.refund.payment
          if (
            payment.subscription ||
            payment.processor_payment_data.subscription_id
          ) {
            return <RecurringCell />
          }
          return null
        },
      },
      {
        Header: 'Status',
        id: 'status',
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original
          let payment = payoutLineItem.payment || payoutLineItem.refund.payment
          let className = 'label-light-success'
          let text = General.snakeCaseToTitleCase(payment.status)
          if(payment.status === "pending"){
            className = 'label-light-info'
          }
          else if(payment.status === "failed"){
            className = 'label-light-danger'
          }
          if(payment.refunded_at){
            text = General.snakeCaseToTitleCase(payment.refund_status || "Refunded")
            className = 'label-light-warning'
          }
          return <PillCell text={text} className={className} />
        },
      },
      {
        Header: 'Date Paid',
        id: 'paid_at',
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original
          let payment = payoutLineItem.payment || payoutLineItem.refund.payment

          return moment(payment.paid_at).format('DD MMM YYYY HH:mm') || '-'
        }
      },
      {
        Header: 'Refund Date',
        id: 'deleted_at',
        Cell: (rowInfo) => {
          const payoutLineItem = rowInfo.original
          let payment = payoutLineItem.payment || payoutLineItem.refund.payment

          return payment.refunded_at ? moment(payment.refunded_at).format('DD MMM YYYY') : '-'
        }
      },
    ]

    return columns
  }

  _renderDescription(payment){
    const {
      payment_instrument: paymentInstrument,
      transaction_details: transactionDetails,
      processor_payment_data,
      processor_payment_id,
      processor_payment_status,
      status,
      customer,
      payment_plan: paymentPlan,
      products,
      answers,
      fees_synced,
      fees,
      net
    } = payment

    const paymentMethod = paymentInstrument?.payment_method

    const Detail = ({ label, children }) => (
      <p className="mb-0" style={{whiteSpace: "pre-wrap"}}>
        <b>{label}:</b> {children}
      </p>
    )

    const Section = ({ title, children }) => (
      <div className="mb-3">
        <h6>{title}</h6>
        {children}
      </div>
    )

    const shippingAddress = customer?.shipping_address

    const formatAddress = (address) => [address.line_1, address.line_2, address.city, address.state, address.country,].filter(Boolean).join(', ')

    const feeDetails = [
      ['Total', Currency.format(paymentPlan.currency.symbol, fees?.total)],
    ]

    const transactionDetailsList = [
      ['Category', transactionDetails?.category],
      ['Card Type', transactionDetails?.card_type],
      ['Card Region', transactionDetails?.card_region],
      ['Funding Source', transactionDetails?.funding_source],
      ['Shopper Interaction', transactionDetails?.shopper_interaction],
    ]

    const cardDetails = [
      ['Brand', paymentInstrument?.brand],
      ['Variant', paymentInstrument?.variant],
      ['Identifier', paymentInstrument?.identifier],
      ['Cardholder Name', paymentInstrument?.cardholder_name],
      ['Expiry', (paymentInstrument?.expiry_mm && paymentInstrument?.expiry_yy) ? `${paymentInstrument?.expiry_mm}/${paymentInstrument?.expiry_yy}` : ""],
    ]

    return (
      <div style={{backgroundColor: '#fff', paddingTop: 15, paddingLeft: 15}}>
        {processor_payment_data && (
          <p className="mb-3">
            <b>ID:</b> {processor_payment_id}
          </p>
        )}

        {/*{transactionDetails && (*/}
        {/*  <Section title="Transaction Details">*/}
        {/*    {transactionDetailsList.map(([label, value]) => (*/}
        {/*      <Detail key={label} label={label}>*/}
        {/*        {value}*/}
        {/*      </Detail>*/}
        {/*    ))}*/}
        {/*  </Section>*/}
        {/*)}*/}

        {status !== 'completed' && (
          <p className="mb-3" style={{whiteSpace: "pre-wrap"}}>
            <b>Processor Payment Status:</b> {processor_payment_status}
          </p>
        )}

        {
          shippingAddress && (
            <p className="mb-3" style={{whiteSpace: "pre-wrap"}}>
              <b>Customer Address:</b> {formatAddress(shippingAddress)}
            </p>
          )}

        {
          customer?.note && (
            <p className="mb-3" style={{whiteSpace: "pre-wrap"}}>
              <b>Customer Note: </b>
              {customer.note}
            </p>
          )}

        {
          paymentPlan?.payment_link && (
            <p className="mb-3" style={{whiteSpace: "pre-wrap"}}>
              <b>Channel: </b>
              {paymentPlan.payment_link.title}
            </p>
          )}

        {
          (products.length === 0 && answers.length > 0) && (
            <DisplayCDC answers={answers} />
          )}

        {products.length > 0 && (
          <>
            <h5>Products</h5>

            {
              products.map((product) => {
                const total = product.product_total * product.quantity

                const price = Currency.format(paymentPlan.currency.symbol, total)

                const unitPrice = Currency.format(paymentPlan.currency.symbol, product.product_total)

                const name = product.quantity > 1 ? `${product.quantity}x ${product.name}` : product.name

                const formattedPrice = product.quantity > 1 ? `${price} (${unitPrice} each)` : price

                return (
                  <div key={product.id} className="mb-3">
                    <hr />
                    <p>
                      <b>Product: </b>
                      { name }
                    </p>
                    <p>
                      <b>Price: </b>
                      { formattedPrice }
                    </p>
                    <DisplayCDC answers={product.answers} />
                  </div>
                )
              })
            }
          </>
        )}

        {
          fees_synced &&
          <p className="mb-3">
            <b>Net:</b> {Currency.format(paymentPlan.currency.symbol, net)}
          </p>
        }

        {fees_synced && fees && (
          <Section title="Fees">
            {feeDetails.map(([label, value]) => (
              <Detail key={label} label={label}>
                {value || "-"}
              </Detail>
            ))}
          </Section>
        )}

        {(["card", "pos", "soft_pos"].includes(paymentMethod?.type)) && (
          <Section title="Card Details">
            {cardDetails.map(([label, value]) => (
              <Detail key={label} label={label}>
                {value || "-"}
              </Detail>
            ))}
          </Section>
        )}
      </div>
    )
  }

  render() {
    let { dateFrom, dateTo, payout } = this.state

    const columns = this._getColumns()

    let endpoint = window.Api.PayoutLineItems

    return (
      <div>
        <BaseTable
          key={this.props.type}
          ref={this.table}
          endpoint={endpoint}
          deletedObjectLabel="Refunded"
          noDataMessage={'No pay out line items found'}
          title={this.props.title}
          columns={columns}
          params={{
            min_date: dateFrom?.format('YYYY-MM-DD') || '',
            max_date: dateTo?.format('YYYY-MM-DD') || '',
            payout_id: payout?.id || '',
          }}
          showExport={false}
          defaultSorted={[
            {
              id: 'created_at',
              desc: true,
            },
          ]}
          showHeader={false}
          showFilter={false}
          showSearch={false}
          showPaginationBottom={true}
          SubComponent={(row) => {
            const payoutLineItem = row.original
            let payment = payoutLineItem.payment || payoutLineItem.refund.payment
            return this._renderDescription(payment)
          }}
        />
      </div>
    )
  }
}

PayoutLineItemsTable.defaultProps = {
  type: null,
  latestResults: false,
  canShowEngagementCard: true,
}
